// src/components/DeckSelector.tsx - Enhanced with deck lock awareness

import React, { useState, useEffect } from 'react';
import { deckApi } from '../lib/deckApi';
import { PlayerDeck, DeckChoice } from '../types/index';
import { useAuth } from '../context/AuthContext';

interface DeckSelectorProps {
  onDeckSelect: (deckChoice: DeckChoice) => void;
  selectedDeck?: DeckChoice;
  disabled?: boolean;
  title?: string;
}

const DeckSelector: React.FC<DeckSelectorProps> = ({
  onDeckSelect,
  selectedDeck = 'deck_1',
  disabled = false,
  title = "Select Your Deck"
}) => {
  const { user } = useAuth();
  const [decks, setDecks] = useState<PlayerDeck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

 useEffect(() => {
  if (!user) return;
  loadDecks();
}, [user]);

const loadDecks = async () => {
  if (!user) return;
  
  try {
    setLoading(true);
    const playerDecks = await deckApi.getPlayerDecks(user.id);
    setDecks(playerDecks);
    
    // REMOVED ALL VALIDATION - DeckSelector should never block game acceptance
    // Validation only belongs in the Deck Builder component
    
  } catch (error) {
    console.error('Error loading decks:', error);
    setError('Failed to load decks');
  } finally {
    setLoading(false);
  }
};

  const getDeckStatus = (deck: PlayerDeck) => {
    const cardCount = deck.cards?.length || 0;
    
    if (deck.isLocked) {
      return { 
        status: 'locked', 
        color: 'text-red-400', 
        label: `🔒 Locked (${cardCount} cards)`,
        reason: `Used in: ${deck.lockInfo?.map(g => `vs ${g.opponent}`).join(', ')}`
      };
    } else if (cardCount === 0) {
      return { status: 'empty', color: 'text-red-400', label: 'Empty', reason: 'No cards in deck' };
    } else if (cardCount < 5) {
      return { 
        status: 'insufficient', 
        color: 'text-yellow-400', 
        label: `Only ${cardCount} cards`,
        reason: 'Need at least 5 cards to play'
      };
    } else {
      return { status: 'ready', color: 'text-green-400', label: `${cardCount} cards`, reason: 'Ready to play' };
    }
  };

  const canSelectDeck = (deck: PlayerDeck) => {
    
    return true;
  };

  const getViableDecksCount = () => {
  return decks.filter(deck => (deck.cards?.length || 0) >= 5).length;
  };

  if (loading) {
    return (
      <div className="deck-selector-loading">
        <div className="text-center text-gray-400">Loading decks...</div>
      </div>
    );
  }

 

  const viableDecks = getViableDecksCount();

  return (
    <div className="deck-selector">
      <h4 className="text-lg font-semibold text-white mb-4">{title}</h4>
      

      <div className="space-y-3">
        {/* Individual Deck Options */}
        {decks.map(deck => {
          const status = getDeckStatus(deck);
          const isSelectable = canSelectDeck(deck);
          const deckChoice = `deck_${deck.deck_slot}` as DeckChoice;
          const isSelected = selectedDeck === deckChoice;

          return (
            <label
              key={deck.deck_slot}
              className={`
                deck-option flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all
                ${isSelected ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 bg-gray-800/50'}
                ${!isSelectable ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-500'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${deck.isLocked ? 'border-red-500 bg-red-900/10' : ''}
              `}
              title={deck.isLocked ? status.reason : undefined}
            >
              <input
                type="radio"
                name="deck-choice"
                value={deckChoice}
                checked={isSelected}
                onChange={() => isSelectable && !disabled && onDeckSelect(deckChoice)}
                disabled={!isSelectable || disabled}
                className="sr-only"
              />
              
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h5 className="font-semibold text-white flex items-center gap-2">
                    {deck.isLocked && <span className="text-red-400">🔒</span>}
                    {deck.deck_name}
                  </h5>
                  <span className={`text-sm font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </div>
                
                {/* Lock Information */}
                {deck.isLocked && deck.lockInfo && deck.lockInfo.length > 0 && (
                  <div className="mt-1 text-xs text-red-300">
                    Currently used in: {deck.lockInfo.map(game => `vs ${game.opponent} (${game.status})`).join(', ')}
                  </div>
                )}
                
                {/* Card Preview */}
                {deck.cards && deck.cards.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {deck.cards.slice(0, 5).map(card => (
                      <span
                        key={card.id}
                        className={`text-xs px-2 py-1 rounded ${
                          deck.isLocked ? 'bg-red-700 text-red-300' : 'bg-gray-700 text-gray-300'
                        }`}
                      >
                        {card.name}
                      </span>
                    ))}
                    {deck.cards.length > 5 && (
                      <span className="text-xs text-gray-400">
                        +{deck.cards.length - 5} more
                      </span>
                    )}
                  </div>
                )}
                
                {/* Status Messages */}
                {!isSelectable && (
                  <p className="text-xs text-red-400 mt-1">
                    {deck.isLocked ? 'Deck is locked - used in active games' :
                     status.status === 'empty' ? 'Add cards to this deck' : 'Need at least 5 cards'}
                  </p>
                )}
              </div>

              {/* Selection Indicator */}
              {isSelected && (
                <div className="ml-4 text-blue-400">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </label>
          );
        })}

        {/* Random Option - Only if viable decks exist */}
        {viableDecks > 0 && (
          <label
            className={`
              deck-option flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all
              ${selectedDeck === 'random' ? 'border-purple-500 bg-purple-900/20' : 'border-gray-600 bg-gray-800/50'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-500'}
            `}
          >
            <input
              type="radio"
              name="deck-choice"
              value="random"
              checked={selectedDeck === 'random'}
              onChange={() => !disabled && onDeckSelect('random')}
              disabled={disabled}
              className="sr-only"
            />
            
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h5 className="font-semibold text-white">🎲 Random Deck</h5>
                <span className="text-sm font-medium text-purple-400">
                  Surprise me!
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Randomly select from your {viableDecks} ready deck{viableDecks !== 1 ? 's' : ''}
              </p>
            </div>

            {selectedDeck === 'random' && (
              <div className="ml-4 text-purple-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </label>
        )}
      </div>

      {/* Deck Building Reminder */}
      {viableDecks < 3 && (
        <div className="mt-4 p-3 bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-300">
            💡 <strong>Tip:</strong> Build multiple decks for different strategies! 
            You currently have {viableDecks}/3 ready decks.
          </p>
          <button
            onClick={() => window.location.href = '/deck-builder'}
            className="mt-2 text-blue-400 hover:text-blue-300 text-sm underline"
          >
            Build More Decks →
          </button>
        </div>
      )}

      {/* Lock Information */}
      {decks.some(d => d.isLocked) && (
        <div className="mt-4 p-3 bg-red-900/20 border border-red-600 rounded-lg">
          <p className="text-sm text-red-300">
            <strong>🔒 Locked Decks:</strong> Some of your decks are currently locked for editing because they're being used in active games. You can still select these decks to accept this game.
            They will automatically unlock when linked games are finished.
          </p>
        </div>
      )}
    </div>
  );
};

export default DeckSelector;